import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { SocialLinksFormFields } from "@/components/admin/SocialLinksFormFields";
import { AdminImagePicker } from "@/components/admin/AdminImagePicker";
import { ImageLightbox } from "@/components/ImageLightbox";
import { supabase } from "@/lib/supabase";
import { resolveEntitySlug } from "@/lib/slug";
import { uploadPublicImage } from "@/lib/upload";
import { usePortalUserContext } from "@/admin/PortalUserContext";
import {
  batchHasPublicDetailPage,
  batchPublicPrimaryName,
  batchShownOnPublicList,
} from "@/lib/batchDisplay";
import { adminListRowHighlightClass } from "@/lib/adminListRowHighlight";
import { useFileObjectUrl } from "@/lib/useFileObjectUrl";
import {
  adminAddNewSecondaryButtonClass,
  adminFormSectionClass,
  adminListSectionClass,
  adminScopedFormSectionClass,
} from "@/lib/adminSectionChrome";
import { socialUrlOrNull } from "@/lib/entitySocialLinks";
import { portalHasSiteWideWrite, portalIsSuperUser } from "@/lib/portalSession";

type Row = {
  id: string;
  slug: string;
  logo_url: string | null;
  official_title: string | null;
  custom_title: string | null;
  ol_year: number | null;
  al_year: number | null;
  description: string | null;
  exco_photo_url: string | null;
  social_facebook_url: string | null;
  social_instagram_url: string | null;
  social_youtube_url: string | null;
  social_website_url: string | null;
  social_other_url: string | null;
  projects?: { count: number }[];
  news?: { count: number }[];
};

type BatchListFilter =
  | "all"
  | "on_public_list"
  | "not_on_public_list"
  | "detail_page_live";

function batchSlugSource(
  customTitle: string,
  officialTitle: string,
  alYear: string,
  olYear: string
): string {
  return (
    customTitle.trim() ||
    officialTitle.trim() ||
    (alYear.trim() ? `batch-${alYear.trim()}` : "") ||
    (olYear.trim() ? `ol-${olYear.trim()}` : "")
  );
}

export function BatchesTab() {
  const portal = usePortalUserContext();
  const siteWide = portal ? portalHasSiteWideWrite(portal.grants) : false;
  const canEditBatchName = portal ? portalIsSuperUser(portal.grants) : false;
  const batchWorkspaces = useMemo(
    () => portal?.assignments.filter((a) => a.kind === "batch") ?? [],
    [portal?.assignments]
  );
  const activeBatchWorkspace = useMemo(() => {
    if (!portal || batchWorkspaces.length === 0) return null;
    const hit = batchWorkspaces.find((a) => a.id === portal.activeAssignmentId);
    return hit ?? batchWorkspaces[0];
  }, [portal, batchWorkspaces]);
  const scopeBatchId = activeBatchWorkspace?.parent_id ?? null;

  const [rows, setRows] = useState<Row[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [officialTitle, setOfficialTitle] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [olYear, setOlYear] = useState("");
  const [alYear, setAlYear] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [excoUrl, setExcoUrl] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [excoFile, setExcoFile] = useState<File | null>(null);
  const [usernameUrl, setUsernameUrl] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialWebsite, setSocialWebsite] = useState("");
  const [socialOther, setSocialOther] = useState("");
  const [msg, setMsg] = useState("");
  const [listFilter, setListFilter] = useState<BatchListFilter>("all");
  const excoFilePreview = useFileObjectUrl(excoFile);

  const load = useCallback(async () => {
    let q = supabase
      .from("batches")
      .select("*, projects(count), news(count)");
    if (scopeBatchId) q = q.eq("id", scopeBatchId);
    const { data } = await q.order("al_year", { ascending: false, nullsFirst: false });
    setRows((data as Row[]) ?? []);
  }, [scopeBatchId]);

  const filteredRows = useMemo(() => {
    switch (listFilter) {
      case "on_public_list":
        return rows.filter((r) => batchShownOnPublicList(r));
      case "not_on_public_list":
        return rows.filter((r) => !batchShownOnPublicList(r));
      case "detail_page_live":
        return rows.filter((r) => batchHasPublicDetailPage(r));
      default:
        return rows;
    }
  }, [rows, listFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (siteWide || rows.length !== 1) return;
    startEdit(rows[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteWide, rows]);

  function startNew() {
    setEditingId(null);
    setOfficialTitle("");
    setCustomTitle("");
    setOlYear("");
    setAlYear("");
    setDescription("");
    setLogoUrl("");
    setExcoUrl("");
    setLogoFile(null);
    setExcoFile(null);
    setUsernameUrl("");
    setSocialFacebook("");
    setSocialInstagram("");
    setSocialYoutube("");
    setSocialWebsite("");
    setSocialOther("");
    setMsg("");
  }

  function startEdit(r: Row) {
    setEditingId(r.id);
    setOfficialTitle(r.official_title ?? "");
    setCustomTitle(r.custom_title ?? "");
    setOlYear(r.ol_year != null ? String(r.ol_year) : "");
    setAlYear(r.al_year != null ? String(r.al_year) : "");
    setDescription(r.description ?? "");
    setLogoUrl(r.logo_url ?? "");
    setExcoUrl(r.exco_photo_url ?? "");
    setLogoFile(null);
    setExcoFile(null);
    setUsernameUrl(r.slug);
    setSocialFacebook(r.social_facebook_url ?? "");
    setSocialInstagram(r.social_instagram_url ?? "");
    setSocialYoutube(r.social_youtube_url ?? "");
    setSocialWebsite(r.social_website_url ?? "");
    setSocialOther(r.social_other_url ?? "");
    setMsg("");
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setMsg("");
    try {
      let nextLogo = logoUrl.trim();
      let nextExco = excoUrl.trim();
      if (logoFile) nextLogo = await uploadPublicImage(logoFile, "batches/logos");
      if (excoFile) nextExco = await uploadPublicImage(excoFile, "batches/exco");
      const officialTitlePersisted =
        editingId && !canEditBatchName
          ? (rows.find((r) => r.id === editingId)?.official_title ?? null)
          : officialTitle || null;
      const source = batchSlugSource(
        customTitle,
        canEditBatchName ? officialTitle : officialTitlePersisted ?? "",
        alYear,
        olYear
      );
      const finalSlug = resolveEntitySlug(usernameUrl, source, "batch");
      const payload = {
        slug: finalSlug,
        official_title: officialTitlePersisted,
        custom_title: customTitle || null,
        ol_year: olYear ? parseInt(olYear, 10) : null,
        al_year: alYear ? parseInt(alYear, 10) : null,
        description: description || null,
        logo_url: nextLogo || null,
        exco_photo_url: nextExco || null,
        social_facebook_url: socialUrlOrNull(socialFacebook),
        social_instagram_url: socialUrlOrNull(socialInstagram),
        social_youtube_url: socialUrlOrNull(socialYoutube),
        social_website_url: socialUrlOrNull(socialWebsite),
        social_other_url: socialUrlOrNull(socialOther),
      };
      if (editingId) {
        const { error } = await supabase.from("batches").update(payload).eq("id", editingId);
        if (error) throw error;
        setMsg("Updated.");
      } else {
        if (!siteWide) {
          setMsg("Only full admins can create batches.");
          return;
        }
        const { error } = await supabase.from("batches").insert(payload);
        if (error) throw error;
        setMsg("Created.");
      }
      setLogoFile(null);
      setExcoFile(null);
      void load();
      startNew();
    } catch (err) {
      console.error(err);
      setMsg("Error saving (check Username / URL is unique).");
    }
  }

  async function onDelete(id: string) {
    if (!siteWide) return;
    if (!confirm("Delete batch and its projects?")) return;
    const { error } = await supabase.from("batches").delete().eq("id", id);
    if (error) console.error(error);
    void load();
    if (editingId === id) startNew();
  }

  const batchForm = (
    <form onSubmit={onSave} className="mt-5 space-y-4">
      {scopeBatchId && !siteWide ? (
        <p className="text-xs text-brand-blue/70">
          You can update this batch&apos;s page content. Creating batches or deleting them requires a full
          admin.
        </p>
      ) : null}
        <div className="space-y-1">
          <label htmlFor="batch-official-title" className="block text-xs font-semibold text-brand-blue">
            Name
          </label>
          <input
            id="batch-official-title"
            autoComplete="off"
            placeholder="e.g. Batch 2009"
            value={officialTitle}
            onChange={(e) => setOfficialTitle(e.target.value)}
            disabled={!canEditBatchName}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-brand-gray/40 disabled:opacity-70"
          />
          {!canEditBatchName ? (
            <p className="text-xs text-brand-blue/60">Only a super user can change the batch name.</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <label htmlFor="batch-custom-title" className="block text-xs font-semibold text-brand-blue">
            Custom name
          </label>
          <input
            id="batch-custom-title"
            autoComplete="off"
            placeholder="Optional (e.g. Group of 76) — can be added later"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <div className="w-1/2 space-y-1">
            <label htmlFor="batch-ol-year" className="block text-xs font-semibold text-brand-blue">
              O/L year
            </label>
            <input
              id="batch-ol-year"
              inputMode="numeric"
              placeholder="Year"
              value={olYear}
              onChange={(e) => setOlYear(e.target.value)}
              className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
            />
          </div>
          <div className="w-1/2 space-y-1">
            <label htmlFor="batch-al-year" className="block text-xs font-semibold text-brand-blue">
              A/L year
            </label>
            <input
              id="batch-al-year"
              inputMode="numeric"
              placeholder="Year"
              value={alYear}
              onChange={(e) => setAlYear(e.target.value)}
              className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label htmlFor="batch-description" className="block text-xs font-semibold text-brand-blue">
            Description
          </label>
          <textarea
            id="batch-description"
            placeholder="About this batch"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <AdminImagePicker
          label="Logo"
          inputId="batch-logo"
          remoteUrl={logoUrl}
          selectedFile={logoFile}
          onFileChange={setLogoFile}
          onClear={() => {
            setLogoFile(null);
            setLogoUrl("");
          }}
          imgClassName="h-20 max-h-24 w-auto max-w-[12rem] rounded border border-brand-gray/60 bg-white object-contain p-1"
        />
        <div className="space-y-1">
          <label htmlFor="batch-exco" className="block text-xs font-semibold text-brand-blue">
            ExCo photo
          </label>
          <p className="text-xs text-brand-blue/60 mb-2">
            Preview fits the image inside the box (wide banners stay full width). Click the preview
            to open the full image.
          </p>
          {excoFilePreview || excoUrl ? (
            <div className="mb-2 flex flex-wrap items-end gap-3 max-w-xl">
              <ImageLightbox
                src={excoFilePreview ?? excoUrl}
                thumbMaxHeightClass="max-h-40"
                hint="Click for full size"
              />
              <button
                type="button"
                onClick={() => {
                  setExcoFile(null);
                  setExcoUrl("");
                }}
                className="shrink-0 text-xs font-bold uppercase tracking-wide text-red-700 hover:underline"
              >
                Remove image
              </button>
            </div>
          ) : null}
          <input
            id="batch-exco"
            type="file"
            accept="image/*"
            onChange={(e) => setExcoFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-brand-blue/10 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-blue"
          />
        </div>
        <SocialLinksFormFields
          fieldIdPrefix="batch"
          facebookUrl={socialFacebook}
          instagramUrl={socialInstagram}
          youtubeUrl={socialYoutube}
          websiteUrl={socialWebsite}
          otherUrl={socialOther}
          onFacebookUrl={setSocialFacebook}
          onInstagramUrl={setSocialInstagram}
          onYoutubeUrl={setSocialYoutube}
          onWebsiteUrl={setSocialWebsite}
          onOtherUrl={setSocialOther}
        />
        <div className="space-y-1">
          <label htmlFor="batch-username-url" className="block text-xs font-semibold text-brand-blue">
            Username / URL <span className="font-normal text-brand-blue/60">(optional)</span>
          </label>
          <input
            id="batch-username-url"
            autoComplete="off"
            placeholder="Leave blank to auto-generate from titles / years"
            value={usernameUrl}
            onChange={(e) => setUsernameUrl(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
          <p className="text-xs text-brand-blue/60">
            Public URL segment under <span className="font-mono">/alumni/batches/</span>. Uses custom
            title, then official title, then years, when left blank.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!editingId && !siteWide}
            className="rounded-full bg-brand-maroon px-6 py-2 text-sm font-bold uppercase text-white disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            onClick={startNew}
            className="rounded-full border border-brand-gray px-4 py-2 text-sm"
          >
            Clear
          </button>
        </div>
        {msg && <p className="text-sm">{msg}</p>}
    </form>
  );

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-brand-blue">Batches</h2>

      {siteWide ? (
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          <section aria-labelledby="batches-list-heading" className={adminListSectionClass}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-gray pb-3">
              <h3 id="batches-list-heading" className="font-display text-lg font-bold text-brand-blue">
                Batch list
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (editingId) startNew();
                }}
                disabled={!editingId}
                title={
                  !editingId
                    ? "You are already adding a new batch — use the form on the right"
                    : "Clear the editor and add a new batch"
                }
                className={adminAddNewSecondaryButtonClass}
              >
                Add new batch
              </button>
            </div>
            <div className="mt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-blue/70">
                Filter
              </p>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Filter batch list"
              >
                {(
                  [
                    { id: "all" as const, label: "All batch" },
                    { id: "on_public_list" as const, label: "Listed" },
                    { id: "not_on_public_list" as const, label: "Not listed" },
                    { id: "detail_page_live" as const, label: "Listed with page" },
                  ] satisfies { id: BatchListFilter; label: string }[]
                ).map(({ id, label }) => {
                  const active = listFilter === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setListFilter(id)}
                      aria-pressed={active}
                      className={
                        active
                          ? "rounded-full bg-brand-maroon px-4 py-2 text-sm font-semibold text-white shadow-sm"
                          : "rounded-full border border-brand-gray bg-white px-4 py-2 text-sm font-semibold text-brand-blue shadow-sm transition hover:border-brand-maroon/45 hover:bg-brand-maroon/5"
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-brand-blue/60">
                “Public list” matches the site: custom name, description, logo, ExCo photo, project, or
                news. <span className="font-medium text-brand-blue/75">Listed with page</span> means a public batch page is live
                (project, news, or ExCo photo).
              </p>
            </div>
            <div className="mt-4 max-h-[min(70vh,36rem)] overflow-auto rounded-lg border border-brand-gray/80 bg-brand-gray/30 p-2">
              <ul className="space-y-1">
                {filteredRows.length === 0 && rows.length > 0 ? (
                  <li className="px-3 py-6 text-center text-sm text-brand-blue/65">
                    No batches match this filter.
                  </li>
                ) : null}
                {filteredRows.map((r, rowIndex) => {
                  const selected = editingId === r.id;
                  const onList = batchShownOnPublicList(r);
                  const detailLive = batchHasPublicDetailPage(r);
                  const n = rowIndex + 1;
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-3 transition-colors ${adminListRowHighlightClass(selected)}`}
                    >
                      <span className="w-10 shrink-0 text-right text-sm font-medium tabular-nums text-brand-blue/55">
                        {n}
                      </span>
                      <span className="min-w-0 flex-1 font-medium text-brand-blue">
                        <span className="flex flex-wrap items-center gap-2">
                          {batchPublicPrimaryName(r)}
                          {onList ? (
                            <span className="rounded border border-emerald-600/40 bg-emerald-50 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                              List
                            </span>
                          ) : (
                            <span className="rounded border border-amber-600/35 bg-amber-50 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-amber-900">
                              Not listed
                            </span>
                          )}
                          {detailLive ? (
                            <span className="rounded border border-brand-blue/30 bg-brand-blue/5 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-brand-blue">
                              Page
                            </span>
                          ) : null}
                        </span>
                        {r.custom_title?.trim() ? (
                          <span className="block truncate text-sm font-normal text-brand-blue/70">
                            {r.custom_title.trim()}
                          </span>
                        ) : null}
                      </span>
                      <div className="flex shrink-0 flex-row items-center gap-4">
                        <button
                          type="button"
                          className="text-xs font-bold uppercase tracking-wide text-brand-maroon"
                          onClick={() => startEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs font-bold uppercase tracking-wide text-red-700"
                          onClick={() => void onDelete(r.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
          <section aria-labelledby="batches-form-heading" className={adminFormSectionClass}>
            <div className="border-b border-brand-gray pb-3">
              <h3 id="batches-form-heading" className="font-display text-lg font-bold text-brand-blue">
                {editingId ? "Edit batch" : "Add new batch"}
              </h3>
              {editingId && (customTitle.trim() || officialTitle.trim()) ? (
                <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">
                  {customTitle.trim() || officialTitle.trim()}
                </p>
              ) : null}
            </div>
            {batchForm}
          </section>
        </div>
      ) : (
        <>
          <section aria-labelledby="batches-form-heading" className={adminScopedFormSectionClass}>
            <div className="border-b border-brand-gray pb-3">
              <h3 id="batches-form-heading" className="font-display text-lg font-bold text-brand-blue">
                {scopeBatchId ? "Your batch" : editingId ? "Edit batch" : "Add new batch"}
              </h3>
              {editingId && (customTitle.trim() || officialTitle.trim()) ? (
                <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">
                  {customTitle.trim() || officialTitle.trim()}
                </p>
              ) : null}
            </div>
            {batchForm}
          </section>
          {scopeBatchId && rows[0] ? (
            <p className="mt-6 text-sm text-brand-blue/80">
              Use the form above to save changes to{" "}
              <strong>{batchPublicPrimaryName(rows[0])}</strong>.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
